import { useMemo, useRef, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import useListController from "@/hooks/list/useListController";
import {
	usePhytosanitaryProgramLookups,
	usePhytosanitaryProgram,
	useSavePhytosanitaryProgram,
	useCreateApplicationOrder,
} from "@/hooks/planning/phytosanitary-program/usePhytosanitaryProgram";
import { usePhytosanitaryProgramTransform } from "@/hooks/planning/phytosanitary-program/usePhytosanitaryProgramTransform";

import { usePhytosanitaryImport } from "./usePhytosanitaryImport";
import { usePhytosanitaryExport } from "./usePhytosanitaryExport";
import { usePhytosanitaryProgramColumns } from "../ProgramColumns/usePhytosanitaryProgramColumns";
import { usePhytosanitaryProgress } from "./usePhytosanitaryProgress";
import { usePhytosanitaryProductRemoval } from "../ApplicationForm/usePhytosanitaryProductRemoval";
import { usePhytosanitaryApplicationDeletion } from "../ApplicationForm/usePhytosanitaryApplicationDeletion";
import { usePhytosanitaryApplicationFormController } from "../ApplicationForm/usePhytosanitaryApplicationFormController";
import { usePhytosanitaryApplicationForm } from "../../components/ApplicationForm/usePhytosanitaryApplicationForm";
import usePhytosanitaryProgramFilters from "./usePhytosanitaryProgramFilters";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";

import { base_filter, base_table } from "../../phytosanitary_program.constants";
import { SCREEN_ID_PHYTOSANITARY_PROGRAM } from "@/constants/screenIds";

export default function usePhytosanitaryProgramController() {
	const location = useLocation();
	const navigate = useNavigate();
	const inputRefExcel = useRef(null);

	const { estate_id, estate_parent_id } = useSelector((s) => s.auth.filter);

	const { canShow, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_PHYTOSANITARY_PROGRAM);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const [editingRowId, setEditingRowId] = useState(null);
	const [infoMessages, setInfoMessages] = useState([]);
	const [messageType, setMessageType] = useState("info");
	const [isProgressModalOpen, setProgressModalOpen] = useState(false);

	const clearMessages = () => setInfoMessages([]);

	const {
		isFilterOpen,
		filtersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: base_filter,
		baseTable: base_table,
	});

	const { data: lookups, isLoading: isLoadingLookups } = usePhytosanitaryProgramLookups({ estate_id, estate_parent_id }, { enabled: canLoadScreenData });

	const activeSpeciesId = appliedFilterParams.specie_selector ?? null;

	const filtersForQuery = useMemo(() => {
		const filters = { ...appliedFilterParams };

		if (activeSpeciesId !== null && activeSpeciesId !== undefined) {
			filters.specie = activeSpeciesId;
		}

		return filters;
	}, [appliedFilterParams, activeSpeciesId]);

	const { data, isLoading, isFetching } = usePhytosanitaryProgram(
		{ ...filtersForQuery, estate_id, estate_parent_id, ...tableState },
		{
			enabled:
				canLoadScreenData &&
				!isLoadingLookups &&
				Boolean(lookups) &&
				filtersForQuery.specie !== undefined &&
				filtersForQuery.specie !== null &&
				filtersForQuery.specie !== "",
		},
	);

	const transformedData = usePhytosanitaryProgramTransform(data?.data);

	const { progressData, totalProgress, totalSurface } = usePhytosanitaryProgress(filtersForQuery, estate_id, estate_parent_id, isLoadingLookups, lookups);

	const { isLoadingImport, showConfirmModal, validationData, handleImportClick, handleFileChange, handleConfirmImport, handleCancelImport } =
		usePhytosanitaryImport(inputRefExcel, setInfoMessages, setMessageType);

	const { mutate: save, isLoading: isLoadingSave } = useSavePhytosanitaryProgram();
	const { mutate: createOA, isLoading: isLoadingCreateOA } = useCreateApplicationOrder();

	const applicationForm = usePhytosanitaryApplicationForm();

	const handleCreateOA = useCallback(
		(row) => {
			const formData = {
				phytosanitary_program_id: Number(row.id),
				application_number: Number(row.application_number),
				application_order_date: new Date().toISOString(),
				observations: "",
			};

			createOA(formData, {
				onSuccess: (response) => {
					toast.success(response?.message || "Orden de aplicación creada exitosamente");
					navigate(`/orden_aplicacion/editar/${response.application_order_id}`);
				},
				onError: (error) => {
					if (error?.response?.data?.messages && error.response.data.messages.length > 0) {
						setInfoMessages(error.response.data.messages);
						setMessageType(error.response.data.message_type || "error");
					} else {
						toast.error(error?.response?.data?.message || "Error al crear orden de aplicación");
					}
				},
			});
		},
		[createOA, navigate],
	);

	const handleSave = useCallback(
		(params) => {
			const { recordId, fieldName, newValue, originalRow } = params || {};

			const queryFilters = {
				...filtersForQuery,
				program: filtersForQuery.program != null ? String(filtersForQuery.program) : "",
				specie: Number(filtersForQuery.specie),
				phenological_state: filtersForQuery.phenological_state != null ? String(filtersForQuery.phenological_state) : "",
				objective: filtersForQuery.objective != null ? String(filtersForQuery.objective) : "",
				dealer_id: filtersForQuery.dealer_id != null ? String(filtersForQuery.dealer_id) : "",
				variety: filtersForQuery.variety != null ? String(filtersForQuery.variety) : "",
				type: filtersForQuery.type != null ? String(filtersForQuery.type) : "",
				estate_id,
				estate_parent_id,
				...tableState,
			};

			save(
				{
					id: recordId,
					field: fieldName,
					value: String(newValue),
					phytosanitary_program_id: Number(originalRow.id),
					application_number: Number(originalRow.application_number),
					product_id: Number(originalRow.product_id),
					filters: queryFilters,
				},
				{
					onSuccess: (response) => {
						if (response?.message) {
							toast.success(response.message);
						} else {
							toast.success("Registro actualizado correctamente");
						}
					},
					onError: (error) => {
						if (error?.response?.data?.messages && error.response.data.messages.length > 0) {
							setInfoMessages(error.response.data.messages);
							setMessageType(error.response.data.message_type || "error");
						} else {
							toast.error(error?.response?.data?.message || "Error al guardar");
						}
					},
				},
			);
		},
		[filtersForQuery, estate_id, estate_parent_id, tableState, save],
	);

	const { isConfirmModalOpen, productToRemove, handleRemoveProduct, confirmRemoveProduct, cancelRemoveProduct } = usePhytosanitaryProductRemoval({
		handleSave,
	});

	const { applicationToDelete, isLoadingDeleteApplication, handleDeleteApplication, confirmDeleteApplication, cancelDeleteApplication } =
		usePhytosanitaryApplicationDeletion({ setInfoMessages, setMessageType, programData: transformedData });

	const {
		applicationFormMode,
		applicationFormRow,
		applicationFormRows,
		isIrrigation,
		isLoadingAddRows,
		handleNewApplication,
		handleAddProduct,
		closeApplicationForm,
		handleSubmitApplicationForm,
	} = usePhytosanitaryApplicationFormController({
		applicationForm,
		lookups,
		filtersForQuery,
		estateId: estate_id,
		estateParentId: estate_parent_id,
		tableState,
		programData: transformedData,
		setInfoMessages,
		setMessageType,
	});

	const { handleExport } = usePhytosanitaryExport(appliedFilterParams, tableState);

	usePhytosanitaryProgramFilters({
		lookups,
		appliedFilterParams,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
		handleClearOneFilter,
		location,
	});

	const columns = usePhytosanitaryProgramColumns(lookups, handleSave, editingRowId, setEditingRowId, handleRemoveProduct, handleCreateOA, {
		handleDeleteApplication,
		handleAddProduct,
		estateParentId: estate_parent_id,
	});

	return {
		navigate,
		inputRefExcel,
		lookups,
		isLoading,
		isFetching,
		isLoadingPermissions,
		canShow,
		canEdit,
		canDelete,
		isLoadingLookups,
		isLoadingSave,
		isLoadingCreateOA,
		isLoadingImport,
		isLoadingDeleteApplication,
		isLoadingAddRows,
		data,
		transformedData,
		columns,
		tableState,
		reactTableSort,
		filtersState,
		isFilterOpen,
		infoMessages,
		messageType,
		clearMessages,
		openFilter,
		closeFilter,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		handleFilterChange,
		activeSpeciesId,
		handleExport,
		totalProgress,
		totalSurface,
		isProgressModalOpen,
		setProgressModalOpen,
		progressData,
		showConfirmModal,
		validationData,
		handleImportClick,
		handleFileChange,
		handleConfirmImport,
		handleCancelImport,
		isConfirmModalOpen,
		productToRemove,
		confirmRemoveProduct,
		cancelRemoveProduct,
		applicationToDelete,
		confirmDeleteApplication,
		cancelDeleteApplication,
		applicationFormMode,
		applicationFormRow,
		applicationFormRows,
		isIrrigation,
		handleNewApplication,
		closeApplicationForm,
		handleSubmitApplicationForm,
		applicationForm,
	};
}
